import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Layout } from "../../containers";
import { GameContextSelector } from "../../components";
import { Container } from "./styled";
import { calculatePercj, calculatePlayerStats } from "../../lib/cabbStats";
import { Scatter } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  annotationPlugin
);

function ScatterPlots({ appData }) {
  const [gameContextData, setGameContextData] = useState({
    activeLeague: null,
    activePhase: null,
    activeSeason: null,
    activeTeam: null,
    activeRival: null,
    baseTeam: null,
    games: null,
    statInX: null,
    statInY: null,
    isLoading: false,
  });
  const [dataSet, setDataset] = useState([]);

  const footer = (tooltipItems) => {
    let off = 0;
    let deff = 0;
    let name;
    tooltipItems.forEach(function (tooltipItem) {
      off = tooltipItem.parsed.x;
      deff = tooltipItem.parsed.y;
      name = tooltipItem.raw.label;
    });
    return `${name} ${gameContextData.statInX}: ${off}, ${gameContextData.statInY}: ${deff}`;
  };

  // const handleGameContextChange = useCallback((contextData) => {
  //   setGameContextData(contextData);
  // }, []);

  // Después
  const handleGameContextChange = useCallback((contextData) => {
    setGameContextData({ ...contextData }); // fuerza nueva referencia
  }, []);

  useEffect(() => {
    let teamsArr = [];
    let data = [];
    let labels = [];
    if (
      gameContextData.games &&
      gameContextData.games.length > 0 &&
      gameContextData.statInX !== null &&
      gameContextData.statInY !== null
    ) {
      appData.teams?.forEach((element) => {
        if (element.league === gameContextData.activeLeague) {
          teamsArr.push({
            _id: element._id,
            name: element.name,
            xValue: calculatePercj(
              gameContextData.games.filter(
                (game) =>
                  game.season === gameContextData.activeSeason &&
                  (gameContextData.activePhase === null ||
                    game.stage === gameContextData.activePhase)
              ),
              element._id,
              gameContextData.statInX
            ).toFixed(1),
            yValue: calculatePercj(
              gameContextData.games.filter(
                (game) =>
                  game.season === gameContextData.activeSeason &&
                  (gameContextData.activePhase === null ||
                    game.stage === gameContextData.activePhase)
              ),
              element._id,
              gameContextData.statInY
            ).toFixed(1),
          });
        }
      });

      teamsArr.sort((a, b) => b.value - a.value);
      teamsArr.forEach((e) => {
        labels.push(e.name);
        data.push({
          x: parseFloat(e.xValue),
          y: parseFloat(e.yValue),
          label: e.name,
        });
      });
      setDataset(data);
    } else {
      setDataset([]);
    }
  }, [
    appData,
    gameContextData,
    gameContextData.games,
    gameContextData.activeLeague,
    gameContextData.activePhase,
    gameContextData.statInX,
    gameContextData.statInY,
  ]);

  // Calcular valores para el gráfico solo si hay datos
  const minYAxisValue =
    dataSet.length > 0 ? Math.min(...dataSet.map((v) => v.y)) - 3 : 0;
  const maxYAxisValue =
    dataSet.length > 0 ? Math.max(...dataSet.map((v) => v.y)) + 3 : 100;
  const maxXAxisValue =
    dataSet.length > 0 ? Math.max(...dataSet.map((v) => v.x)) + 3 : 100;
  const minXAxisValue =
    dataSet.length > 0 ? Math.min(...dataSet.map((v) => v.x)) - 3 : 0;

  let offA = 0;
  let deffA = 0;

  // Calcular promedios
  dataSet.forEach((v) => {
    offA += v.y;
    deffA += v.x;
  });

  const averageY = dataSet.length > 0 ? offA / dataSet.length : 0;
  const averageX = dataSet.length > 0 ? deffA / dataSet.length : 0;

  const annotation1 = {
    type: "line",
    borderColor: "gray",
    borderWidth: 2,
    borderDash: [10, 10],
    label: {
      backgroundColor: "red",
      content: "Promedio Defensivo",
      display: true,
    },
    scaleID: "left-y-axis",
    value: averageY,
  };

  const annotation2 = {
    type: "line",
    borderColor: "lightgray",
    borderWidth: 2,
    borderDash: [10, 10],
    label: {
      backgroundColor: "red",
      content: "Promedio Ofensivo",
      display: true,
    },
    scaleID: "right-x-axis",
    value: averageX,
  };

  const options = {
    type: "scatter",
    backgroundColor: "red",
    scales: {
      "left-y-axis": {
        type: "linear",
        position: "left",
        min: minYAxisValue,
        max: maxYAxisValue,
        reverse: gameContextData.statInY === "DFE" ? true : false,
      },
      "right-x-axis": {
        type: "linear",
        position: "bottom",
        min: minXAxisValue,
        max: maxXAxisValue,
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          footer: footer,
        },
      },
      annotation: {
        annotations: {
          annotation1,
          annotation2,
        },
      },
    },
  };

  function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [breakpoint]);

    return isMobile;
  }
  const isMobile = useIsMobile();
  const imageSize = isMobile ? 25 : 50;

  const dataChart = {
    datasets: [
      {
        label: `${gameContextData.statInX} VS ${gameContextData.statInY}`,
        data: dataSet.map((v) => ({ x: v.x, y: v.y, label: v.label })),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 1)",
        pointStyle: dataSet.map((png) => {
          const image = new Image(imageSize, imageSize);
          image.src = `/assets/teams-logos/${png.label}.png`;
          return image;
        }),
      },
    ],
  };

  return (
    <Layout>
      <Container>
        <GameContextSelector
          appData={appData}
          typeOfSelector="scatterPlots"
          onChange={handleGameContextChange}
        />

        {gameContextData.isLoading && <div>Cargando datos...</div>}

        {!gameContextData.isLoading && dataSet.length > 0 && (
          <Scatter options={options} data={dataChart} />
        )}

        {!gameContextData.isLoading &&
          dataSet.length === 0 &&
          gameContextData.games && (
            <div>No hay datos disponibles para mostrar el gráfico.</div>
          )}

        {!gameContextData.games && !gameContextData.isLoading && (
          <div>Selecciona una liga y temporada para ver el gráfico.</div>
        )}
      </Container>
    </Layout>
  );
}

export default ScatterPlots;
